// =============================================================================
// Public Signing API Service
// =============================================================================
// Handles signing ceremony API calls. These endpoints do NOT require JWT auth.
// Authentication is via the signing token in the URL.
// Uses a separate axios instance without auth interceptors.
// =============================================================================

import axios from 'axios';
import type { SigningSessionData, SignatureMethod } from '../types/esign';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const signingApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

const signingService = {
  /**
   * Initialize signing session - loads document info, fields, consent status.
   */
  async initializeSession(token: string): Promise<SigningSessionData> {
    const response = await signingApi.get(`/signing/${token}`);
    return response.data.data;
  },

  /**
   * Get the document PDF as a blob URL for rendering.
   */
  async getDocumentUrl(token: string): Promise<string> {
    const response = await signingApi.get(`/signing/${token}/document`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },

  /**
   * Accept ESIGN Act consent.
   */
  async acceptConsent(token: string, consentVersion: string): Promise<{ consentGivenAt: string; canProceed: boolean }> {
    const response = await signingApi.post(`/signing/${token}/consent`, {
      consentAccepted: true,
      consentVersion,
    });
    return response.data.data;
  },

  /**
   * Record that the signer viewed a specific page.
   */
  async recordPageView(token: string, pageNumber: number, durationSeconds?: number): Promise<{
    pagesViewed: number;
    allPagesViewed: boolean;
  }> {
    const response = await signingApi.post(`/signing/${token}/pages/${pageNumber}/view`, {
      durationSeconds,
    });
    return response.data.data.progress;
  },

  /**
   * Submit a text/checkbox/date field value.
   */
  async submitFieldValue(token: string, fieldId: string, value: string): Promise<{
    fieldId: string;
    value: string;
    completedAt: string;
  }> {
    const response = await signingApi.post(`/signing/${token}/fields/${fieldId}`, { value });
    return response.data.data;
  },

  /**
   * Submit a signature or initials image.
   */
  async submitSignature(
    token: string,
    fieldId: string,
    signatureData: string,
    signatureType: SignatureMethod,
    saveForFuture = false
  ): Promise<{ fieldId: string; signatureType: string; completedAt: string }> {
    const response = await signingApi.post(`/signing/${token}/fields/${fieldId}/signature`, {
      signatureType,
      signatureData,
      adoptionAcknowledged: true,
      saveForFuture,
    });
    return response.data.data;
  },

  /**
   * Complete the signing ceremony.
   */
  async completeSigning(token: string): Promise<{
    signerStatus: string;
    envelopeStatus: string;
    message: string;
    nextSigner?: { name: string; email: string } | null;
  }> {
    const response = await signingApi.post(`/signing/${token}/complete`, {
      confirmCompletion: true,
    });
    return response.data.data;
  },

  /**
   * Decline to sign.
   */
  async declineSigning(token: string, reason: string): Promise<{
    signerStatus: string;
    envelopeStatus: string;
    message: string;
  }> {
    const response = await signingApi.post(`/signing/${token}/decline`, {
      reason,
      confirmDecline: true,
    });
    return response.data.data;
  },
};

export default signingService;
